import React, { useEffect, useMemo } from 'react';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import Loading from 'components/Loading';
import { useLocalStorageState } from 'ahooks';
import useDataSource from 'hooks/useDataSource';
import useUser from 'hooks/useUser';
import Sidebar from './Sidebar';
import Routes from './Routes';
import { routesToMenuItems } from './utils';

const { Content } = Layout;

const findFirstRoute = (routes) => {
  for (const route of routes) {
    if (route.label && route.path && route.link !== false) {
      return route;
    }
    if (route.children) {
      const childRoute = findFirstRoute(route.children);
      if (childRoute) {
        return childRoute;
      }
    }
  }
  return null;
};

const pathIsInRoutes = (pathname, routes) => routes?.some((route) => {
  if (route.path) {
    const prefix = route.path.endsWith('/*') ? route.path.slice(0, -2) : route.path;
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return true;
    }
  }
  return pathIsInRoutes(pathname, route.children);
});

const RoutedLayout = ({
  routes,
  sidebar,
  className,
  loading,
  children,
  persistKey,
}) => {
  const history = useHistory();
  const { url: baseUrl } = useRouteMatch();
  const { pathname: currentPath } = useLocation();
  const [lastPath, setLastPath] = useLocalStorageState(`lastPath-${baseUrl}${persistKey ? `-${persistKey}` : ''}`);
  const { hasDataSourcePermission } = useDataSource();
  const { hasPermission } = useUser();

  const hasAccessToRoute = r => (!r.dataSourcePermission || hasDataSourcePermission(r.dataSourcePermission))
    && (!r.permission || hasPermission(r.permission));

  const mapRoute = (route, parentPath) => {
    const routePath = route.link || `${parentPath}/${route.path}`;

    return {
      ...route,
      path: routePath,
      children: route.children?.filter(hasAccessToRoute).map(r => mapRoute(r, routePath)),
    };
  };

  const mappedRoutes = useMemo(() => routes?.filter(hasAccessToRoute).map(r => mapRoute(r, baseUrl)), [baseUrl, hasDataSourcePermission, hasPermission, routes]);

  useEffect(() => {
    if (currentPath !== baseUrl) {
      if (pathIsInRoutes(currentPath, mappedRoutes) && lastPath !== currentPath && !currentPath.endsWith('settings')) {
        setLastPath(currentPath);
      }
    } else if (lastPath && pathIsInRoutes(lastPath, mappedRoutes)) {
      history.replace(lastPath);
    } else {
      const firstRoute = findFirstRoute(mappedRoutes);
      if (firstRoute) {
        history.replace(firstRoute.path);
      }
    }
  }, [history, lastPath, setLastPath, currentPath, mappedRoutes, baseUrl]);

  const menu = useMemo(() => ({
    items: routesToMenuItems({ routes: mappedRoutes }),
    defaultOpenKeys: mappedRoutes?.filter(r => r.defaultOpen)?.map(r => r.path),
  }), [mappedRoutes]);

  return (
    <Layout className={className}>
      {sidebar && <Sidebar menu={menu} {...(sidebar !== true ? sidebar : {})} />}
      <Content>
        {loading ? <Loading /> : (
          children || <Routes routes={mappedRoutes} />
        )}
      </Content>
    </Layout>
  );
};

export default RoutedLayout;
